import { Stack, Typography } from '@mui/material';

import { ContractTypeOptions } from '~/data/schemas/contract';
import { formatCurrency as formatCurrencyValue } from '~/utils/format';

import type { ContractReview } from './types';

// Shared presentational helpers for the contract detail drawer tabs
// (Overview, Coverage, Rates, KPIs).

export const Row = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <Stack direction="row" justifyContent="space-between" spacing={2} sx={{ py: 0.5 }}>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 500, textAlign: 'right' }}>
      {value}
    </Typography>
  </Stack>
);

// null = unknown (e.g. fireService not specified on the contract).
export const yesNo = (value: boolean | null): string =>
  value === null ? 'Unknown' : value ? 'Yes' : 'No';

export const formatCurrency = ({
  value,
  currencyCode,
}: {
  value: number;
  currencyCode: string;
}): string =>
  formatCurrencyValue({ value: value > 0 ? value : null, currencyCode, nullValue: '—' });

// Parse YYYY-MM-DD as a local date. `new Date('2024-12-31')` is UTC midnight,
// which shifts to the prior day for US timezones once localized — wrong for
// date-only fields like expirationDate/startDate from the API.
const parseIsoDate = (iso: string): Date => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (match) {
    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  }
  return new Date(iso);
};

export const formatDate = (iso: string): string =>
  parseIsoDate(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

// Days from today until the given date; negative once the date has passed.
// The renewal/expiry chips derive from contract.expirationDate rather than a
// stored countdown — production has no days-to-renewal column.
//
// Returns null (never NaN) for a missing or unparseable date: expiration is a
// DateField(null=True), so a real contract can have no expiration, and
// `new Date('')`/`new Date(null)` would otherwise yield an Invalid Date that
// renders as a "NaN d" chip (AMP-585).
export const daysUntil = (isoDate: string | null | undefined): number | null => {
  if (!isoDate) return null;
  const target = parseIsoDate(isoDate);
  if (Number.isNaN(target.getTime())) return null;
  const today = new Date();

  // Count calendar days apart, not elapsed milliseconds: a local span crossing a DST
  // boundary is 23 or 25 hours, and dividing by 24 leaves a remainder the rounding turns
  // into a whole extra day — a contract expiring in exactly 120 days read as 121.
  // Date.UTC has no DST, so these differ by an exact number of days.
  const targetDay = Date.UTC(target.getFullYear(), target.getMonth(), target.getDate());
  const todayDay = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.round((targetDay - todayDay) / (1000 * 60 * 60 * 24));
};

// Render an ISO date for display, or an em dash when the date is absent — the
// review view model carries null dates through (see daysUntil) rather than ''.
export const displayDate = (iso: string | null | undefined): string => iso || '—';

// The stored `Contract.type` rendered for a reader. Django's `choices` is not a DB
// constraint and the column has no default, so a real row can hold '' or a value outside
// the enum: label what we recognise, pass anything else through as-is, and dash an empty
// type.
export const contractTypeLabel = (type: string): string => ContractTypeOptions[type] || type || '—';

// Total elevators covered across the contract's properties. Production has no
// contract-level car count; it is a roll-up of the per-property counts.
export const contractCarCount = (contract: ContractReview): number =>
  contract.properties.reduce((sum, p) => sum + p.carCount, 0);

// Total monthly price across the contract's properties. Production stores
// monthly_price per PropertyContract, not on the Contract.
export const contractMonthlyPrice = (contract: ContractReview): number =>
  contract.properties.reduce((sum, p) => sum + p.monthlyPrice, 0);
